import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/react';
import { AppShell } from '@/components/AppShell';
import { useBusinesses } from '@/context/BusinessContext';
import {
  ApiError,
  createTransaction,
  getAccounts,
  getSaleCategories,
  getTaxCodes,
  getTransactions,
  type Account,
  type PaymentMethod,
  type SaleCategory,
  type TaxCode,
  type Transaction,
  type TransactionEntry,
  type TransactionHeader,
} from '@/lib/api';

// ---- money / date helpers -------------------------------------------------

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Parse a dollars string (e.g. "100", "100.5", "100.50") to integer cents. */
function parseDollarsToCents(input: string): number | null {
  const s = input.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  return Number(whole) * 100 + Number((frac + '00').slice(0, 2));
}

/** Today's calendar date in SI-local time (UTC+11), to match the reference clock. */
function todayISO(): string {
  return new Date(Date.now() + 11 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK', label: 'Into a bank account' },
  { value: 'CREDIT', label: 'On credit (owed to us)' },
];

const inputClass =
  'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 ' +
  'placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100';
const labelClass = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300';
const cardClass =
  'rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900';

// ---- component ------------------------------------------------------------

export function IncomeSales() {
  const { getToken } = useAuth();
  const { activeBusinessId, activeBusiness } = useBusinesses();
  const currency = activeBusiness?.currency_code ?? 'SBD';

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [saleCategories, setSaleCategories] = useState<SaleCategory[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [refsError, setRefsError] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(true);

  // form state
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CASH');
  const [settlementAccountId, setSettlementAccountId] = useState('');
  const [taxInclusive, setTaxInclusive] = useState(true);
  const [quantity, setQuantity] = useState('');
  const [reference, setReference] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [txnDate, setTxnDate] = useState(todayISO());

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{
    transaction: TransactionHeader;
    entries: TransactionEntry[];
  } | null>(null);

  const cashAccounts = useMemo(
    () => accounts.filter((a) => a.cash_bank_kind === 'CASH'),
    [accounts],
  );
  const bankAccounts = useMemo(
    () => accounts.filter((a) => a.cash_bank_kind === 'BANK'),
    [accounts],
  );
  const selectedCategory = useMemo(
    () => saleCategories.find((c) => c.id === selectedCategoryId) ?? null,
    [saleCategories, selectedCategoryId],
  );
  // The category's bound tax code (resolved against the tax codes the form
  // already loads), or null when the category carries no tax.
  const categoryTaxCode = useMemo(() => {
    if (!selectedCategory?.tax_code_id) return null;
    return taxCodes.find((t) => t.id === selectedCategory.tax_code_id) ?? null;
  }, [selectedCategory, taxCodes]);
  const categoryHasTax = categoryTaxCode !== null;
  const categoryIsPerUnit = categoryTaxCode?.rate_kind === 'PER_UNIT';
  const unitLabel = categoryTaxCode?.unit_label ?? 'units';
  const quantityValue = Number(quantity);
  const quantityValid = quantity.trim() !== '' && Number.isFinite(quantityValue) && quantityValue > 0;
  // Preview of the per-unit tax (unit amount × quantity), for the helper line.
  const perUnitTaxCents =
    categoryIsPerUnit && quantityValid
      ? Math.round((categoryTaxCode?.unit_amount_cents ?? 0) * quantityValue)
      : 0;

  const accountLabel = useCallback(
    (id: string) => {
      const a = accounts.find((x) => x.id === id);
      return a ? `${a.code} ${a.name}` : id;
    },
    [accounts],
  );

  const loadRefs = useCallback(async () => {
    if (!activeBusinessId) return;
    setLoadingRefs(true);
    setRefsError(null);
    try {
      const token = await getToken();
      const [accs, codes, cats] = await Promise.all([
        getAccounts(token, activeBusinessId),
        getTaxCodes(token, activeBusinessId),
        getSaleCategories(token, activeBusinessId),
      ]);
      setAccounts(accs);
      setTaxCodes(codes);
      setSaleCategories(cats);

      setSelectedCategoryId(cats[0]?.id ?? '');
      // Settlement account is chosen by the keep-valid effect below, which
      // picks the first account matching the current payment method.
    } catch (err) {
      setRefsError(
        err instanceof ApiError ? err.message : 'Could not load sale categories.',
      );
    } finally {
      setLoadingRefs(false);
    }
  }, [activeBusinessId, getToken]);

  const loadTransactions = useCallback(async () => {
    if (!activeBusinessId) return;
    setLoadingList(true);
    try {
      const token = await getToken();
      setTransactions(await getTransactions(token, activeBusinessId, 'INCOME'));
    } catch {
      // A failed list refresh is non-fatal; leave the prior list in place.
    } finally {
      setLoadingList(false);
    }
  }, [activeBusinessId, getToken]);

  useEffect(() => {
    void loadRefs();
    void loadTransactions();
  }, [loadRefs, loadTransactions]);

  // Keep the chosen settlement account consistent with the payment method:
  // when the method changes (or accounts load), snap to the first account of
  // the matching kind if the current pick no longer applies.
  useEffect(() => {
    if (paymentMethod === 'CREDIT') return;
    const opts = paymentMethod === 'CASH' ? cashAccounts : bankAccounts;
    if (opts.length > 0 && !opts.some((a) => a.id === settlementAccountId)) {
      setSettlementAccountId(opts[0].id);
    }
  }, [paymentMethod, cashAccounts, bankAccounts, settlementAccountId]);

  const settlementOptions =
    paymentMethod === 'CASH'
      ? cashAccounts
      : paymentMethod === 'BANK'
        ? bankAccounts
        : [];

  const amountCents = parseDollarsToCents(amount);
  const canSubmit =
    !submitting &&
    selectedCategoryId !== '' &&
    amountCents !== null &&
    amountCents > 0 &&
    (!categoryIsPerUnit || quantityValid) &&
    (paymentMethod === 'CREDIT' || settlementAccountId !== '');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (!activeBusinessId) return;
    if (selectedCategoryId === '') {
      setSubmitError('Choose what you sold.');
      return;
    }
    if (amountCents === null || amountCents <= 0) {
      setSubmitError('Enter an amount greater than zero.');
      return;
    }
    if (categoryIsPerUnit && !quantityValid) {
      setSubmitError(`Enter the number of ${unitLabel}.`);
      return;
    }
    if (categoryIsPerUnit && taxInclusive && amountCents < perUnitTaxCents) {
      setSubmitError(
        `The tax (${formatCents(perUnitTaxCents)}) is more than the amount entered — check the amount or the number of ${unitLabel}.`,
      );
      return;
    }
    if (paymentMethod !== 'CREDIT' && settlementAccountId === '') {
      setSubmitError('Choose the account the money went into.');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getToken();
      const result = await createTransaction(token, {
        business_id: activeBusinessId,
        module: 'INCOME',
        transaction_date: txnDate,
        amount_cents: amountCents,
        sale_category_id: selectedCategoryId,
        income_account_id: null,
        payment_method: paymentMethod,
        settlement_account_id: paymentMethod === 'CREDIT' ? null : settlementAccountId,
        tax_code_id: null,
        tax_inclusive: categoryHasTax ? taxInclusive : false,
        quantity: categoryIsPerUnit ? quantityValue : null,
        counterparty: counterparty.trim() || null,
        description: null,
        reference: reference.trim() || null,
      });
      setLastResult(result);
      setAmount('');
      setQuantity('');
      setReference('');
      setCounterparty('');
      await loadTransactions();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Could not record the sale.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell title="Income / Sales">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* ---- Record a sale ---- */}
        <section className="lg:col-span-3">
          <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Record a sale
          </h2>

          {refsError && (
            <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
              {refsError}
            </p>
          )}

          {loadingRefs ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
          ) : saleCategories.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              This business has no sale categories set up.
            </p>
          ) : (
            <form className={cardClass} onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className={labelClass} htmlFor="sale-category">
                    What did you sell?
                  </label>
                  {saleCategories.length === 1 ? (
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                      {saleCategories[0].name}
                    </div>
                  ) : (
                    <select
                      id="sale-category"
                      className={inputClass}
                      value={selectedCategoryId}
                      onChange={(e) => {
                        setSelectedCategoryId(e.target.value);
                        setQuantity('');
                      }}
                    >
                      {saleCategories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {categoryHasTax
                      ? `${categoryTaxCode?.name} applies to this sale.`
                      : 'No tax applies to this sale.'}
                  </p>
                </div>

                {categoryIsPerUnit && (
                  <div className="sm:col-span-2">
                    <label className={labelClass} htmlFor="quantity">
                      Number of {unitLabel}
                    </label>
                    <input
                      id="quantity"
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      className={inputClass}
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      placeholder={`e.g. 100 ${unitLabel}`}
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {quantityValid
                        ? `Tax: ${formatCents(perUnitTaxCents)} (${formatCents(
                            categoryTaxCode?.unit_amount_cents ?? 0,
                          )} × ${quantityValue} ${unitLabel})${
                            taxInclusive ? ', included in the amount.' : ', added on top of the amount.'
                          }`
                        : `This tax is charged per ${unitLabel.replace(/s$/, '')}.`}
                    </p>
                  </div>
                )}

                <div>
                  <label className={labelClass} htmlFor="amount">
                    Amount ({currency})
                  </label>
                  <input
                    id="amount"
                    className={inputClass}
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelClass} htmlFor="date">
                    Date
                  </label>
                  <input
                    id="date"
                    type="date"
                    className={inputClass}
                    value={txnDate}
                    onChange={(e) => setTxnDate(e.target.value)}
                  />
                </div>

                <div className="sm:col-span-2">
                  <span className={labelClass}>How were you paid?</span>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {PAYMENT_OPTIONS.map((opt) => {
                      const selected = paymentMethod === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setPaymentMethod(opt.value)}
                          aria-pressed={selected}
                          className={
                            'rounded-md border px-3 py-2 text-sm font-medium transition ' +
                            (selected
                              ? 'border-brand-600 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-950 dark:text-brand-300'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800')
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {paymentMethod !== 'CREDIT' && (
                  <div className="sm:col-span-2">
                    <span className={labelClass}>
                      {paymentMethod === 'CASH'
                        ? 'Into which cash account?'
                        : 'Into which bank account?'}
                    </span>
                    {settlementOptions.length === 0 ? (
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        No {paymentMethod === 'CASH' ? 'cash' : 'bank'} account is set up for
                        this business.
                      </p>
                    ) : settlementOptions.length === 1 ? (
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        {settlementOptions[0].code} {settlementOptions[0].name}
                      </div>
                    ) : (
                      <select
                        id="settlement-account"
                        className={inputClass}
                        value={settlementAccountId}
                        onChange={(e) => setSettlementAccountId(e.target.value)}
                      >
                        {settlementOptions.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} {a.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                {categoryHasTax && (
                  <div className="sm:col-span-2 rounded-md border border-slate-200 px-3 py-3 dark:border-slate-700">
                    <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-600"
                        checked={taxInclusive}
                        onChange={(e) => setTaxInclusive(e.target.checked)}
                      />
                      Amount already includes {categoryTaxCode?.name ?? 'tax'}
                    </label>
                  </div>
                )}

                <div>
                  <label className={labelClass} htmlFor="counterparty">
                    Customer <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="counterparty"
                    className={inputClass}
                    placeholder="e.g. Joe's Store"
                    value={counterparty}
                    onChange={(e) => setCounterparty(e.target.value)}
                  />
                </div>

                <div>
                  <label className={labelClass} htmlFor="reference">
                    Reference <span className="font-normal text-slate-400">(optional)</span>
                  </label>
                  <input
                    id="reference"
                    className={inputClass}
                    placeholder="auto-generated"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                  />
                </div>
              </div>

              {submitError && (
                <p className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="mt-4 inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? 'Recording…' : 'Record sale'}
              </button>
            </form>
          )}

          {lastResult && (
            <ConfirmationPanel
              currency={currency}
              transaction={lastResult.transaction}
              entries={lastResult.entries}
              accountLabel={accountLabel}
              onDismiss={() => setLastResult(null)}
            />
          )}
        </section>

        {/* ---- Recent sales ---- */}
        <section className="lg:col-span-2">
          <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Recent sales
          </h2>
          <div className={cardClass}>
            {loadingList && transactions.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
            ) : transactions.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">No sales recorded yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {transactions.map((t) => {
                  const gross = t.entries.reduce((s, e) => s + e.debit_cents, 0);
                  return (
                    <li key={t.id} className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                          {t.counterparty ?? t.description}
                        </p>
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                          {t.transaction_date} · {t.reference ?? '—'}
                        </p>
                      </div>
                      <span className="shrink-0 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {currency} {formatCents(gross)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

// ---- confirmation panel ---------------------------------------------------

function ConfirmationPanel({
  currency,
  transaction,
  entries,
  accountLabel,
  onDismiss,
}: {
  currency: string;
  transaction: TransactionHeader;
  entries: TransactionEntry[];
  accountLabel: (id: string) => string;
  onDismiss: () => void;
}) {
  return (
    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-5 dark:border-emerald-900 dark:bg-emerald-950">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
            Sale recorded
          </p>
          <p className="text-xs text-emerald-700 dark:text-emerald-400">
            Reference {transaction.reference ?? '—'} — the double entry we posted:
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="text-emerald-700 transition hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-200"
        >
          ✕
        </button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-emerald-700/70 dark:text-emerald-400/70">
            <th className="py-1 font-medium">Account</th>
            <th className="py-1 text-right font-medium">Debit</th>
            <th className="py-1 text-right font-medium">Credit</th>
          </tr>
        </thead>
        <tbody className="text-slate-800 dark:text-slate-200">
          {entries.map((e) => (
            <tr key={e.id}>
              <td className="py-1">{accountLabel(e.account_id)}</td>
              <td className="py-1 text-right tabular-nums">
                {e.debit_cents ? `${currency} ${formatCents(e.debit_cents)}` : ''}
              </td>
              <td className="py-1 text-right tabular-nums">
                {e.credit_cents ? `${currency} ${formatCents(e.credit_cents)}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
